import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { tripService } from '../services/tripService';
import { eventBridgeService, EVENT_TYPES } from '../services/eventService';
import { getSharedTrips } from '../firebase/firestoreService';
import { getUserProfile } from '../services/userProfileService';

const TripContext = createContext();

export const useTrip = () => {
  return useContext(TripContext);
};

// Storage keys
const SELECTED_TRIP_KEY = 'tripPlanner_selectedTrip';
const TRIP_LOCATIONS_KEY = 'tripPlanner_tripLocations';

export const TripProvider = ({ children }) => {
  const [currentTrip, setCurrentTrip] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [tripOwnerId, setTripOwnerId] = useState(null);
  const [tripLocations, setTripLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  // The userId to use for all Firestore data operations on the current trip.
  // For shared trips: the trip owner's UID (data lives under their path).
  // For owned trips: the current user's UID.
  const effectiveUserId = useMemo(() => {
    return tripOwnerId || currentUser?.uid;
  }, [tripOwnerId, currentUser]);

  const isSharedTrip = useMemo(() => {
    return !!tripOwnerId && tripOwnerId !== currentUser?.uid;
  }, [tripOwnerId, currentUser]);

  // Load saved state
  useEffect(() => {
    if (currentUser) {
      try {
        // Load selected trip (supports both old string format and new object format)
        const savedData = localStorage.getItem(`${SELECTED_TRIP_KEY}_${currentUser.uid}`);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (typeof parsed === 'string') {
            // Backward compatibility: old format stored just the tripId string
            setSelectedTripId(parsed);
            setTripOwnerId(null);
          } else if (parsed && typeof parsed === 'object') {
            // New format: { tripId, ownerId }
            setSelectedTripId(parsed.tripId);
            setTripOwnerId(parsed.ownerId || null);
          }
        }

        // Load trip locations
        const savedLocations = localStorage.getItem(`${TRIP_LOCATIONS_KEY}_${currentUser.uid}`);
        if (savedLocations) {
          setTripLocations(JSON.parse(savedLocations));
        }
      } catch (error) {
        console.error('Error loading saved state:', error);
        setTripLocations({});
      }
    } else {
      setSelectedTripId(null);
      setTripOwnerId(null);
      setTripLocations({});
    }
    setLoading(false);
  }, [currentUser]);

  // Load current trip details when selected trip changes
  useEffect(() => {
    const loadTripDetails = async () => {
      if (!currentUser || !selectedTripId) {
        setCurrentTrip(null);
        return;
      }

      // Use effectiveUserId (owner's UID for shared trips, currentUser.uid for owned)
      const fetchUserId = tripOwnerId || currentUser.uid;

      setLoading(true);
      try {
        const trip = await tripService.fetchTrip(fetchUserId, selectedTripId);
        if (trip) {
          // Enrich with sharing metadata if this is a shared trip
          if (tripOwnerId && tripOwnerId !== currentUser.uid) {
            trip.isShared = true;
            trip.sharedBy = tripOwnerId;

            // Fetch owner's email from shared trip pointer
            try {
              const sharedTrips = await getSharedTrips(currentUser.uid);
              const pointer = sharedTrips.find(p =>
                p.originalTripId === selectedTripId && p.ownerId === tripOwnerId
              );

              if (pointer) {
                trip.sharedByEmail = pointer.ownerEmail || null;
                trip.sharedRole = pointer.role || 'viewer';
                if (!trip.sharedByEmail) {
                  console.warn('[TripContext] Shared trip pointer found but missing ownerEmail:', pointer);
                }
              } else {
                console.warn('[TripContext] Could not find shared trip pointer for trip:', selectedTripId);
                trip.sharedByEmail = null;
              }
            } catch (error) {
              console.error('[TripContext] Failed to fetch shared trip pointer:', error);
              trip.sharedByEmail = null;
            }
          }
          setCurrentTrip(trip);
        } else {
          // Only clear if trip not found
          setSelectedTripId(null);
          setTripOwnerId(null);
          setCurrentTrip(null);
          localStorage.removeItem(`${SELECTED_TRIP_KEY}_${currentUser.uid}`);
        }
      } catch (error) {
        console.error('Error loading trip details:', error);
        setError('Failed to load trip details');
        // Don't clear selection on error - might be temporary
      }
      setLoading(false);
    };

    loadTripDetails();
  }, [currentUser, selectedTripId, tripOwnerId]);

  // Listen for trip updates (e.g., when day dates change and trip dates are recalculated)
  useEffect(() => {
    if (!currentUser || !selectedTripId) {
      console.log('[TripContext] Skipping event subscription - no user or trip selected');
      return;
    }

    const fetchUserId = tripOwnerId || currentUser.uid;

    console.log('[TripContext] Setting up TRIP_UPDATED subscription', {
      userId: fetchUserId,
      tripId: selectedTripId
    });

    const subscription = eventBridgeService.subscribe(EVENT_TYPES.TRIP_UPDATED, (payload) => {
      // Match on tripId; for shared trips the event userId may be the owner's UID
      const matchesTripId = payload.tripId === selectedTripId;
      const matchesUserId = payload.userId === currentUser.uid || payload.userId === fetchUserId;

      if (matchesTripId && matchesUserId) {
        console.log('[TripContext] Event matches current trip, refreshing trip data');

        // Refresh trip data from Firestore using the correct path
        tripService.fetchTrip(fetchUserId, selectedTripId)
          .then(async (trip) => {
            if (trip) {
              if (tripOwnerId && tripOwnerId !== currentUser.uid) {
                trip.isShared = true;
                trip.sharedBy = tripOwnerId;

                // Fetch owner's email for display in shared trip context
                try {
                  const ownerProfile = await getUserProfile(tripOwnerId);
                  trip.sharedByEmail = ownerProfile?.email || null;
                } catch (error) {
                  console.error('[TripContext] Failed to fetch owner email on update:', error);
                  trip.sharedByEmail = null;
                }
              }
              setCurrentTrip(trip);
            }
          })
          .catch(error => {
            console.error('[TripContext] Error refreshing trip after update:', error);
          });
      }
    });

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [currentUser, selectedTripId, tripOwnerId]);

  // Save trip locations when they change
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(
        `${TRIP_LOCATIONS_KEY}_${currentUser.uid}`,
        JSON.stringify(tripLocations)
      );
    }
  }, [currentUser, tripLocations]);

  // Save selected trip when it changes
  useEffect(() => {
    if (currentUser) {
      if (selectedTripId) {
        localStorage.setItem(
          `${SELECTED_TRIP_KEY}_${currentUser.uid}`,
          JSON.stringify({ tripId: selectedTripId, ownerId: tripOwnerId })
        );
      } else {
        localStorage.removeItem(`${SELECTED_TRIP_KEY}_${currentUser.uid}`);
      }
    }
  }, [currentUser, selectedTripId, tripOwnerId]);

  const selectTrip = useCallback((tripId, ownerId = null) => {
    // Don't re-select if same trip
    if (tripId === selectedTripId) return;
    setSelectedTripId(tripId);
    setTripOwnerId(ownerId);
  }, [selectedTripId]);

  const clearTripSelection = useCallback(() => {
    setSelectedTripId(null);
    setTripOwnerId(null);
    setCurrentTrip(null);
    if (currentUser) {
      localStorage.removeItem(`${SELECTED_TRIP_KEY}_${currentUser.uid}`);
    }
  }, [currentUser]);

  const updateTripLocation = useCallback((tripId, location) => {
    if (!tripId) return;
    setTripLocations(prev => ({
      ...prev,
      [tripId]: location
    }));
  }, []);

  const value = {
    currentTrip,
    selectedTripId,
    tripOwnerId,
    effectiveUserId,
    isSharedTrip,
    tripLocations,
    loading,
    error,
    selectTrip,
    clearTripSelection,
    updateTripLocation
  };

  return (
    <TripContext.Provider value={value}>
      {children}
    </TripContext.Provider>
  );
};
